import React from 'react';
import ReactDOM from 'react-dom';

//import 'bnet/css/tooltips.css';
import './Tooltip.scss';

function getTooltipRoot() {
  let node = document.getElementById("d2planner-tooltip-root");
  if (node) return node;
  node = document.createElement("div");
  node.id = "d2planner-tooltip-root";
  document.body.appendChild(node);
  return node;
}

function scrollTooltip(evt, tooltip, tipRect, scrollPos) {
  const viewport = {
    left: 5,
    top: 5,
    right: document.documentElement.clientWidth - 5,
    bottom: document.documentElement.clientHeight - 5,
  };

  let clipped = false;
  function clip(val, min, max) {
    if (val < min) {
      val = min;
      clipped = true;
    }
    if (val > max) {
      val = max;
      clipped = true;
    }
    return val;
  }

  if (tipRect.right - tipRect.left > viewport.right - viewport.left) {
    scrollPos.x = clip(scrollPos.x + evt.deltaX, tipRect.left - viewport.left, tipRect.right - viewport.right);
  }
  if (tipRect.bottom - tipRect.top > viewport.bottom - viewport.top) {
    scrollPos.y = clip(scrollPos.y + evt.deltaY, tipRect.top - viewport.top, tipRect.bottom - viewport.bottom);
  }

  tooltip.style.left = (tipRect.left - scrollPos.x) + "px";
  tooltip.style.top = (tipRect.top - scrollPos.y) + "px";

  if (!clipped) {
    evt.preventDefault();
    evt.stopPropagation();
  }
}

function positionTooltip(tooltip, node, align, padding, scrollPos) {
  const rect = node.getBoundingClientRect();
  const viewport = {
    left: 10,
    top: 10,
    right: document.documentElement.clientWidth - 10,
    bottom: document.documentElement.clientHeight - 10,
  };

  const order = ["right", "left", "top", "bottom"];
  if (align) {
    const index = order.indexOf(align);
    if (index >= 0) {
      order[index] = order[0];
      order[0] = align;
    }
  }
  let padX = 5, padY = 5;
  if (padding) {
    padX += padding;
    padY += padding;
  }

  const width = tooltip.offsetWidth;
  const height = tooltip.offsetHeight;
  let horzY = Math.round((rect.top + rect.bottom - height) / 2);
  horzY = Math.min(horzY, viewport.bottom - height);
  horzY = Math.max(horzY, viewport.top);
  let vertX = Math.round((rect.left + rect.right - width) / 2);
  vertX = Math.min(vertX, viewport.right - width);
  vertX = Math.max(vertX, viewport.left);

  let x, y, side;
  for (let i = 0; i < order.length; ++i) {
    if (order[i] === "right") {
      if (rect.right + padX + width <= viewport.right) {
        x = rect.right + padX;
        y = horzY;
        side = "right";
        break;
      }
    } else if (order[i] === "left") {
      if (rect.left - padX - width >= viewport.left) {
        x = rect.left - padX - width;
        y = horzY;
        side = "left";
        break;
      }
    } else if (order[i] === "top") {
      if (rect.top - padY - height >= viewport.top) {
        x = vertX;
        y = rect.top - padY - height;
        side = "top";
        break;
      }
    } else if (order[i] === "bottom") {
      if (rect.bottom + padY + height <= viewport.bottom) {
        x = vertX;
        y = rect.bottom + padY;
        side = "bottom";
        break;
      }
    }
  }
  let overflow = false;
  if (!side) {
    if (viewport.right - rect.right > rect.left - viewport.left) {
      x = rect.right + padX;
      y = horzY;
      side = "right";
    } else {
      x = rect.left - padY - width;
      y = horzY;
      side = "left";
    }
    overflow = true;
  }

  const tipRect = {
    left: x,
    top: y,
    right: x + width,
    bottom: y + height,
    scroll: (width > viewport.right - viewport.left) || (height > viewport.bottom - viewport.top),
    overflow,
  };

  if (width > viewport.right - viewport.left) {
    scrollPos.x = Math.max(Math.min(scrollPos.x, tipRect.right - viewport.right), tipRect.left - viewport.left);
  }
  if (height > viewport.bottom - viewport.top) {
    scrollPos.y = Math.max(Math.min(scrollPos.y, tipRect.bottom - viewport.bottom), tipRect.top - viewport.top);
  }

  tooltip.style.left = (x - scrollPos.x) + "px";
  tooltip.style.top = (y - scrollPos.y) + "px";
  tooltip.className = `d2-tooltip-positioner d2-tooltip-visible d2-tooltip-${side}`;
  //tooltip.style.visibility = "visible";

  return tipRect;
}

function TooltipBody({ tooltip, nodeRef, align, padding, touch, onClose }) {
  const [fullscreen, setFullscreen] = React.useState(false);
  const outer = React.useRef();
  const wrapper = React.useRef();
  const tipRect = React.useRef({scroll: false});
  const tipScroll = React.useRef({x: 0, y: 0});

  const onScroll = React.useCallback(evt => {
    const wrapperNode = wrapper.current && ReactDOM.findDOMNode(wrapper.current);
    if (wrapperNode && tipRect.current.scroll) {
      scrollTooltip(evt, wrapper.current, tipRect.current, tipScroll.current);
    }
  }, []);

  // eslint-disable-next-line
  React.useLayoutEffect(() => {
    if (!fullscreen) {
      const targetNode = nodeRef.current && ReactDOM.findDOMNode(nodeRef.current);
      if (targetNode) {
        tipRect.current = positionTooltip(wrapper.current, targetNode, align, padding, tipScroll.current);
        const link = touch && targetNode.closest("a.d2p-PaperdollContainer")?.href;
        if (touch && (tipRect.current.overflow || link)) {
          setFullscreen(link || true);
        }
      }
    }
  });

  React.useEffect(() => {
    const targetNode = nodeRef.current && ReactDOM.findDOMNode(nodeRef.current);
    if (targetNode) {
      targetNode.addEventListener("wheel", onScroll, {passive: false});
      return () => targetNode.removeEventListener("wheel", onScroll);
    }
  }, [nodeRef, onScroll]);

  const linkRef = React.useRef();
  React.useEffect(() => {
    if (touch/* && !fullscreen*/) {
      function onClick(e) {
        if (!linkRef.current?.contains(e.target)) {
          onClose?.();
        }
      }
      document.addEventListener("click", onClick, true);
      return () => document.removeEventListener("click", onClick, true);
    }
  }, [touch, onClose, fullscreen]);

  const style = {};//visibility: "hidden"};
  if (touch) style.pointerEvents = "auto";

  const isFullscreen = !!fullscreen;
  React.useLayoutEffect(() => {
    if (isFullscreen) {
      const next = wrapper.current.querySelector(".d2-tooltip-wrapper");
      if (!next) return;
      const rect = next.getBoundingClientRect();
      const height = document.documentElement.clientHeight; 
      // hardcoded padding
      if (rect.bottom - rect.top < height - 60) {
        next.style.marginTop = Math.round((height - 60 - (rect.bottom - rect.top)) / 2) + "px";
      }
    }
  }, [isFullscreen]);

  return (
    <div className={"d2planner-tooltip" + (fullscreen ? " d2planner-fullscreen" : "")} ref={outer}>
      <div className="d2-tooltip-positioner" ref={wrapper} style={style}>
        {tooltip}
      </div>
      {isFullscreen && fullscreen !== true && (
        <a ref={linkRef} className="d2p-tooltip-link" href={fullscreen} target="_blank" rel="noopener noreferrer" onClick={onClose}>Open in d2Planner</a>
      )}
    </div>
  );
}

class RefHolder extends React.Component {
  render() {
    return this.props.children;
  }
}

function TooltipInner({ children, tooltip, align, padding, disabled }) {
  const nodeRef = React.useRef();
  const [show, setShow] = React.useState(false);

  const observer = React.useRef();
  const root = React.useRef();
  const target = React.useRef();

  const onMouseLeave = React.useCallback(() => {
    if (observer.current && root.current) {
      observer.current.disconnect();
      root.current = null;
      target.current = null;
    }
    setShow(false);
  }, []);

  const onMouseOver = React.useCallback(e => {
    if (!observer.current && window.MutationObserver) {
      observer.current = new MutationObserver(() => {
        if (root.current && target.current && !root.current.contains(target.current)) {
          onMouseLeave();
        }
      });
    }
    if (observer.current) {
      target.current = e.target;
      root.current = ReactDOM.findDOMNode(nodeRef.current);
      observer.current.observe(root.current, {childList: true, subtree: true});
      //console.log(target.current);
    }
    setShow(true);
  }, [onMouseLeave]);

  React.useEffect(() => {
    return () => {
      if (observer.current) {
        // eslint-disable-next-line
        observer.current.disconnect();
      }
    };
  }, []);

  const touchPos = React.useRef();
  const onTouchStart = React.useCallback(e => {
    if (e.touches?.length === 1) {
      const t = e.touches[0];
      touchPos.current = {x: t.clientX, y: t.clientY};
    } else {
      touchPos.current = null;
    }
  }, []);
  const onTouchEnd = React.useCallback(e => {
    e.preventDefault();
    if (e.touches?.length === 0 && e.changedTouches?.length === 1 && touchPos.current) {
      const t = e.changedTouches[0];
      const dx = Math.abs(t.clientX - touchPos.current.x);
      const dy = Math.abs(t.clientY - touchPos.current.y);
      if (dx < 50 && dy < 50) {
        setShow("touch");
      }
    }
    touchPos.current = null;
    /*if (e.target.tabIndex == null) {
      e.target.tabIndex = -1;
    }
    e.target.focus();
    console.log(document.activeElement);
    function onBlur(blur) {
      setShow(false);
      e.target.removeEventListener("blur", onBlur);
    }
    e.target.addEventListener('blur', onBlur);*/
  }, [setShow]);

  const child = React.Children.only(children);
  const handlers = {onMouseOver, onMouseLeave, onTouchStart, onTouchEnd};
  for (const type in handlers) {
    if (child.props[type]) {
      const orig = handlers[type], other = child.props[type];
      handlers[type] = function() {
        orig.apply(this, arguments);
        other.apply(this, arguments);
      };
    }
  }
  return (
    <React.Fragment>
      <RefHolder ref={nodeRef}>
        {React.cloneElement(child, handlers)}
      </RefHolder>
      {!!show && !disabled && !!tooltip && ReactDOM.createPortal(
        <TooltipBody tooltip={tooltip} nodeRef={nodeRef} align={align} padding={padding} onClose={onMouseLeave} touch={show === "touch"}/>,
        getTooltipRoot()
      )}
    </React.Fragment>
  );
}

export default function Tooltip({ tooltip, ...props }) {
  tooltip = (
    <div className="d2-tooltip-wrapper d2-tooltip-plain">
      <div className="d2p-tooltip-content">{tooltip}</div>
    </div>
  );
  return <TooltipInner {...props} tooltip={tooltip}/>;
}

export function RawTooltip({ tooltip, ...props }) {
  return <TooltipInner {...props} tooltip={tooltip}/>;
}
